import React, { useState, useMemo } from 'react';
import { UserIcon } from '@common_components/icons/user.icon';
import CellCard from 'components/wrapperEventManager/engagementCell.component';
import UserCard from './userCard.component';
import { EventCommitment } from 'interfaces/types.interface';
import { User, UserWithAvatar } from 'interfaces/user.interface';
import { dayjs, FORMAT_DATE_API, RootState, UUID } from '@umanforge/common';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { EventManager } from 'interfaces/eventManager.interface';
import { StyleEventCard } from 'enum/styleEventCard.enum';

interface EventManagerDaysProps {
  days: dayjs.Dayjs[];
  isViewMode?: boolean;
  calendarItem: number;
  dataEventManager: Map<string, Map<string, EventCommitment[]>>;
  getParticipantById: ( id: string ) => ( UserWithAvatar & { eventManagersId: UUID[]; } );
  isDateInMap: ( dateToCheck: dayjs.Dayjs, userId: UUID ) => boolean;
  setEventClick: React.Dispatch<React.SetStateAction<EventCommitment | undefined>>;
  setEventClicked: React.Dispatch<React.SetStateAction<boolean>>;
  setIsDragging: React.Dispatch<React.SetStateAction<boolean>>;
  isArchived: boolean;
  isDragging: boolean;
  eventManager: EventManager;
  themeHightlighted?: string;
  isPopupCreateEventVisible: boolean;
  setIsPopupCreateEventVisible: React.Dispatch<React.SetStateAction<boolean>>;
  refreshEngagementsOfEventManagerForDays: () => void;
  getNextDayNotClosed: ( startDate: dayjs.Dayjs, userId: UUID ) => dayjs.Dayjs;
  isAdminOfEventManager: boolean;
  configuredParticipantOrder?: string[];
  setDateClicked?: React.Dispatch<React.SetStateAction<string | undefined>>;
  setSelectedUserId?: React.Dispatch<React.SetStateAction<string | undefined>>;
  canClickEng?: boolean;
  style?: StyleEventCard;
}

const EventManagerDays: React.FC<EventManagerDaysProps> = ( {
  days,
  isViewMode,
  calendarItem,
  dataEventManager,
  getParticipantById,
  isDateInMap,
  setEventClick,
  setEventClicked,
  setIsDragging,
  isArchived,
  isDragging,
  eventManager,
  themeHightlighted,
  getNextDayNotClosed,
  refreshEngagementsOfEventManagerForDays,
  setIsPopupCreateEventVisible,
  isPopupCreateEventVisible,
  setSelectedUserId,
  isAdminOfEventManager,
  configuredParticipantOrder,
  setDateClicked,
  canClickEng = true,
  style = StyleEventCard.square
} ) => {
  const { t } = useTranslation();

  const me = useSelector( ( state: RootState ) => { return state.user.value; } );
  const [ draggedEngagement, setDraggedEngagement ] = useState<EventCommitment | null>( null );
  const [ draggedParticipantStart, setDraggedParticipantStart ] = useState<User>( { id: '' } );
  const [ userHoverDrag, setUserHoverDrag ] = useState<User>( { id: '1', } );
  const [ dayHoverDragInternal, setDayHoverDragInternal ] = useState<dayjs.Dayjs>( dayjs() );
  const setDayHoverDrag = ( d: Date ) => { setDayHoverDragInternal( dayjs( d ) ); };

  const onClickEngagement = ( event: EventCommitment ) => {
    if ( canClickEng ) {
      setEventClicked( true );
      setEventClick( {
        ...event,
        authorFullName:
          event.authorFullName && event.authorFullName.trim() !== ''
            ? event.authorFullName
            : ( event.participants && event.participants[ 0 ] && event.participants[ 0 ].fullname ? event.participants[ 0 ].fullname : 'Auteur inconnu' ),
        creationDate:
          event.creationDate && event.creationDate.trim() !== ''
            ? event.creationDate
            : ( event.deliveryDate || dayjs().format( 'YYYY-MM-DD' ) )
      } );
    }
  };

  const orderedParticipantIds = useMemo( () => {
    if ( !dataEventManager ) {
      return [];
    }

    if ( configuredParticipantOrder && configuredParticipantOrder.length > 0 ) {
      let ids = configuredParticipantOrder.filter( id => { return dataEventManager.has( id ); } );
      if ( me && me.id && ids.includes( me.id ) ) {
        ids = [ me.id, ...ids.filter( id => { return id !== me.id; } ) ];
      }
      return ids;
    }

    const allParticipantIds = Array.from( dataEventManager.keys() );
    const participantsWithPositions = allParticipantIds.map( id => {
      const participant = getParticipantById( id );
      return {
        id,
        position: participant.position ?? Number.MAX_SAFE_INTEGER
      };
    } ).sort( ( a, b ) => { return a.position - b.position; } );

    let sortedIds = participantsWithPositions.map( p => { return p.id; } );
    // Place me.id en premier si présent
    if ( me && me.id && sortedIds.includes( me.id ) ) {
      sortedIds = [ me.id, ...sortedIds.filter( id => { return id !== me.id; } ) ];
    }
    return sortedIds;
  }, [ dataEventManager, getParticipantById, configuredParticipantOrder, me ] );

  const dataEventManagerWithBacklog = useMemo( () => {
    return dataEventManager;
  }, [ dataEventManager ] );

  return (
    <div
      className='wrapper mb-32 overflow-visible'
      style={{
        display: 'grid',
        gridTemplateColumns: `225px repeat(${calendarItem}, auto)`,
        gridAutoRows: 'auto',
      }}
    >
      <div></div>
      {/* Displaying days */}
      {
        days.map( ( day, index ) => {
          const formattedDay = day && typeof day.format === 'function' ?
            day.startOf( 'day' ).format( 'ddd LL' )
            :
            t( 'translation:invalid_date' );
          return (
            <div key={index} className='flex h-14 items-center justify-center'>
              <div className='text-gray-600 bg-white border-b-2 border-b-orange-400 dark:bg-dark-bg-light dark:text-gray-400 shadow-md w-56 text-center py-1 rounded-t-md font-title'>
                { formattedDay.charAt( 0 ).toUpperCase() + formattedDay.slice( 1 ) }
              </div>
            </div>
          );
        } )
      }

      {
        dataEventManagerWithBacklog != null ?
          <>
            {
              orderedParticipantIds.map( participantId => {
                const eventMap = dataEventManagerWithBacklog.get( participantId ) || new Map();

                const user = getParticipantById( participantId || '' );

                return (
                  <React.Fragment key={participantId}>
                    {/* Displaying Users */}
                    <UserCard style={ style } person={user} />

                    {/* Displaying engagements */}
                    {
                      days.map( day => {
                        const engagementsForDay = ( eventMap.get( day.startOf( 'day' ).format( FORMAT_DATE_API ) ) || [] )
                          .filter( ( engagement: EventCommitment ) => { return engagement.archived === isArchived && engagement.eventManagerIds.includes( eventManager.id ); } );
                        const isDisabled = isDateInMap( day, participantId );

                        return (
                          <CellCard
                            key={ `${ participantId }-${ day.valueOf() }` }
                            isViewArchived={ isArchived }
                            isViewMode={ isViewMode }
                            cellClosed={ isDisabled }
                            themeHightlighted={ themeHightlighted }
                            isDragging={ isDragging }
                            setIsDragging={ setIsDragging }
                            setDraggedParticipantStart={ setDraggedParticipantStart }
                            draggedParticipantStart={ draggedParticipantStart }
                            draggedEngagement={ draggedEngagement }
                            setDraggedEngagement={ setDraggedEngagement }
                            dayOver={ dayHoverDragInternal.toDate() }
                            userOver={ userHoverDrag }
                            setDayOver={ setDayHoverDrag }
                            setUserOver={ setUserHoverDrag }
                            eventManager={ eventManager }
                            dataEventManager={ dataEventManagerWithBacklog }
                            fetchEngagementsOfEventManagerForDays={ refreshEngagementsOfEventManagerForDays }
                            dayCell={ day.toDate() }
                            userCell={ user }
                            engagementsCell={ engagementsForDay }
                            onEngagementClick={ canClickEng ? onClickEngagement : undefined }
                            setIsPopupCreateEventVisible={ setIsPopupCreateEventVisible }
                            isPopupCreateEventVisible={ isPopupCreateEventVisible }
                            setDateClicked={ setDateClicked }
                            setSelectedUserId={ setSelectedUserId }
                            isAdminOfEventManager={ isAdminOfEventManager }
                            getNextDayNotClosed={ getNextDayNotClosed }
                            style={ style }
                          />
                        );
                      } )
                    }
                  </React.Fragment>
                );
              } )
            }
          </>
          :
          <React.Fragment key={'person.id'}>
            {Array( 3 )
              .fill( null )
              .map( ( _, idx ) => {
                return (
                  <React.Fragment key={idx}>
                    <div className='flex h-full flex-center flex-col min-h-48 cursor-pointer'>
                      <UserIcon size={8} className='bg-white shadow dark:bg-dark-bg-light rounded-xl h-20 w-20 p-2' />
                      <div className='text-base mt-3 bg-gray-base border px-8 rounded-full py-2' />
                    </div>
                    {days.map( ( day, index ) => {
                      return (
                        <div
                          key={index}
                          className='trans-fast m-[3px] h-48 bg-[#ffffff] border-white dark:bg-dark-bg-light2 min-w-48 p-2 border-2 flex gap-2 flex-wrap rounded-xl shadow-md items-center justify-center'
                        />
                      );
                    } )}
                  </React.Fragment>
                );
              } )}
          </React.Fragment>
      }
    </div>
  );
};

export default EventManagerDays;
